import { useState } from "react";
import { CalendarClock } from "lucide-react";
import { useToast } from "@/lib/toast";
import { adiarLead } from "@/lib/leads";
import { formatDate } from "@/lib/format";
import type { AdiarLeadResultado } from "@/lib/types";

const OPCOES = [
  { dias: 1, label: "+1 dia" },
  { dias: 3, label: "+3 dias" },
  { dias: 7, label: "+1 semana" },
] as const;

interface AdiarRapidoProps {
  leadId: string;
  empresa: string;
  onAdiado: (resultado: AdiarLeadResultado) => void;
  /** Lead suprimido ou arquivado: o RPC recusa (falha alto, ver
   * migration), entao nem oferecemos o botao nesses casos. */
  disabled?: boolean;
}

/** Adiamento rapido ("+1 dia" / "+3 dias" / "+1 semana"), sempre via RPC
 * adiar_lead — nunca update direto em proximo_contato_em (mesmo gotcha de
 * RLS em UPDATE tratado em db.ts, e o backend fez questao de falhar alto em
 * vez de filtrar em silencio). Reusado na fila, nos leads parados e na
 * ficha do lead. */
export function AdiarRapido({ leadId, empresa, onAdiado, disabled }: AdiarRapidoProps) {
  const { showToast } = useToast();
  const [salvando, setSalvando] = useState<number | null>(null);

  async function adiar(dias: number) {
    setSalvando(dias);
    try {
      const resultado = await adiarLead(leadId, { dias });
      onAdiado(resultado);
      showToast({
        message: `${empresa} adiado para ${formatDate(resultado.proximo_contato_em)}.`,
        tone: "success",
      });
    } catch (e) {
      showToast({ message: `Não foi possível adiar: ${(e as Error).message}`, tone: "error" });
    } finally {
      setSalvando(null);
    }
  }

  return (
    <div className="chip-group" role="group" aria-label={`Adiar próximo contato de ${empresa}`}>
      <span className="field-hint" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <CalendarClock className="icon" size={14} aria-hidden="true" />
        Adiar:
      </span>
      {OPCOES.map((opcao) => (
        <button
          key={opcao.dias}
          type="button"
          className="chip chip--outcome-neutral"
          disabled={disabled || salvando !== null}
          onClick={() => adiar(opcao.dias)}
        >
          <span className="chip-text">{salvando === opcao.dias ? "Adiando…" : opcao.label}</span>
        </button>
      ))}
    </div>
  );
}
